import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getCentroMapaPadrao } from '../lib/estabelecimento';
import { carregarGoogleMaps, TEM_GOOGLE_MAPS } from '../lib/geo';

export interface PinoPublico {
  id: string;
  numero: string;
  titulo: string;
  modulo_codigo: string;
  status: string;
  tipo: string | null;
  endereco: string | null;
  bairro: string | null;
  lat: number;
  lng: number;
  data_abertura: string;
}

export const MODULO_LABEL: Record<string, string> = {
  ouvidoria: 'Ouvidoria',
  esic: 'e-SIC',
  zeladoria: 'Zeladoria',
  protocolo_cidadao: 'Protocolo',
};

export const MODULO_COR: Record<string, string> = {
  ouvidoria: '#2563eb',
  esic: '#7c3aed',
  zeladoria: '#d97706',
  protocolo_cidadao: '#059669',
};

const STATUS_LABEL: Record<string, string> = {
  aberto: 'Aberta', em_tramitacao: 'Em atendimento', em_analise: 'Em análise',
  em_diligencia: 'Em diligência', concluido: 'Resolvida', deferido: 'Deferida',
  indeferido: 'Indeferida', arquivado: 'Arquivada', cancelado: 'Cancelada',
};

const RESOLVIDA = ['concluido', 'arquivado', 'cancelado', 'indeferido'];

interface Props {
  pinos: PinoPublico[];
  altura?: string;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function popupHtml(p: PinoPublico): string {
  const cor = MODULO_COR[p.modulo_codigo] ?? '#64748b';
  const data = new Date(p.data_abertura).toLocaleDateString('pt-BR');
  return `
    <div style="font-family:-apple-system,Segoe UI,Roboto,sans-serif;min-width:220px;">
      <div style="display:flex;gap:6px;align-items:center;">
        <span style="font-size:11px;background:${cor}1a;color:${cor};padding:2px 8px;border-radius:9999px;font-weight:600;">${escapeHtml(MODULO_LABEL[p.modulo_codigo] ?? p.modulo_codigo)}</span>
        <span style="font-size:11px;color:#64748b;">${escapeHtml(p.numero)}</span>
      </div>
      <div style="font-weight:600;color:#0f172a;margin-top:6px;font-size:14px;">${escapeHtml(p.titulo)}</div>
      <div style="margin-top:6px;"><span style="font-size:11px;background:#f1f5f9;color:#475569;padding:2px 8px;border-radius:9999px;">${escapeHtml(STATUS_LABEL[p.status] ?? p.status)}</span></div>
      ${p.endereco ? `<div style="font-size:12px;color:#475569;margin-top:8px;">${escapeHtml(p.endereco)}</div>` : ''}
      ${p.bairro ? `<div style="font-size:12px;color:#64748b;">${escapeHtml(p.bairro)}</div>` : ''}
      <div style="font-size:11px;color:#94a3b8;margin-top:4px;">Aberta em ${data}</div>
    </div>`;
}

/** Mapa público de manifestações. Google quando há chave; senão Leaflet. */
export function MapaManifestacoesPublico(props: Props) {
  return TEM_GOOGLE_MAPS ? <MapaGoogle {...props} /> : <MapaLeaflet {...props} />;
}

function MapaGoogle({ pinos, altura = '70vh' }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const mapsRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoRef = useRef<any>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let vivo = true;
    carregarGoogleMaps().then((maps) => {
      if (!vivo) return;
      if (!maps || !containerRef.current) { setFalhou(true); return; }
      mapsRef.current = maps;
      const c = getCentroMapaPadrao();
      mapRef.current = new maps.Map(containerRef.current, {
        center: { lat: c[0], lng: c[1] }, zoom: 12,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      });
      infoRef.current = new maps.InfoWindow();
      render();
    });
    return () => { vivo = false; mapRef.current = null; markersRef.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { render(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [pinos]);

  function render() {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map) return;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (pinos.length === 0) return;
    const bounds = new maps.LatLngBounds();
    pinos.forEach((p) => {
      const cor = MODULO_COR[p.modulo_codigo] ?? '#64748b';
      const pos = { lat: p.lat, lng: p.lng };
      const marker = new maps.Marker({
        position: pos, map,
        icon: { path: maps.SymbolPath.CIRCLE, scale: 8, fillColor: cor, fillOpacity: RESOLVIDA.includes(p.status) ? 0.45 : 1, strokeColor: '#fff', strokeWeight: 2 },
      });
      marker.addListener('click', () => { infoRef.current.setContent(popupHtml(p)); infoRef.current.open(map, marker); });
      markersRef.current.push(marker);
      bounds.extend(pos);
    });
    if (pinos.length === 1) { map.setCenter({ lat: pinos[0].lat, lng: pinos[0].lng }); map.setZoom(15); }
    else map.fitBounds(bounds, 40);
  }

  if (falhou) return <MapaLeaflet pinos={pinos} altura={altura} />;
  return <div ref={containerRef} style={{ height: altura, width: '100%', borderRadius: '8px' }} />;
}

function MapaLeaflet({ pinos, altura = '70vh' }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView(getCentroMapaPadrao(), 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; markersRef.current = null; };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !markersRef.current) return;
    markersRef.current.clearLayers();
    if (pinos.length === 0) return;
    const bounds = L.latLngBounds(pinos.map((p) => [p.lat, p.lng] as [number, number]));
    pinos.forEach((p) => {
      const cor = MODULO_COR[p.modulo_codigo] ?? '#64748b';
      const resolvida = RESOLVIDA.includes(p.status);
      const icone = L.divIcon({
        className: 'pino-pub',
        html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${cor};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);opacity:${resolvida ? 0.5 : 1};"></div>`,
        iconSize: [22, 22], iconAnchor: [11, 22],
      });
      L.marker([p.lat, p.lng], { icon: icone }).bindPopup(popupHtml(p)).addTo(markersRef.current!);
    });
    if (pinos.length === 1) mapRef.current.setView([pinos[0].lat, pinos[0].lng], 15);
    else mapRef.current.fitBounds(bounds, { padding: [40, 40] });
  }, [pinos]);

  return <div ref={containerRef} style={{ height: altura, width: '100%', borderRadius: '8px' }} />;
}
